import { Component } from "react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import axios from "axios";

import { APP_VERSION, ErrorResponse, getSessionStorage, getValue, isNil, isVersionConflict, setSessionStorage } from "../utils";
import { msalInstance } from "..";
import { b2cScope } from "../constants/Config";
import { utcDateToUser } from "../utils/DateTime";
import { loggingURLArray } from "../constants/ApplicationUrl";
import { showReloadRequiredModal } from "../app/common";
import { MessageType } from "../constants/ResponseMessage";

export const ERR_BAD_REQUEST = "ERR_BAD_REQUEST";
export const ERROR_INVALID_SESSION = "sessionInvalidMessage";
export const ERROR_KEY = "REDIRECT_MESSAGE";
export const ERROR_NOT_FOUND = "notFoundMessage";
export const ERROR_PERMISSION_DENIED = "permissionDeniedMessage";
export const ERROR_SESSION_EXPIRED = "sessionExpiredMessage";
export const FORCE_REDIRECT_KEY = "INVALID_SESSION_FORCE_REDIRECT";
export const NOT_FOUND_ERROR_STATUS = 404;
export const REFRESH_TOKEN_EXPIRED_STATUS = 403;
export const UNAUTHORIZED_ERROR_STATUS = 401;
export const VERSION_CONFLICT_STATUS = 409;
type Methods = "head" | "options" | "put" | "post" | "patch" | "delete" | "get";

// Module-level flag to stop all API calls after 401
let isUnauthorized = false;

export const resetUnauthorizedFlag = () => {
  isUnauthorized = false;
};
class API extends Component {
  static call(method: Methods, url: string, type: any, ...rest: any[]) {
    if (isUnauthorized) return Promise.resolve(undefined);

    return axios[method](url, ...rest)
      .then((response) => {
        resetUnauthorizedFlag();
        const responseData = utcDateToUser(response);
          return responseData;
      })
      .catch((err) => {
        if (isVersionConflict(err.response)) {
          showReloadRequiredModal(VERSION_CONFLICT_STATUS, MessageType.RELOAD_MESSAGE, MessageType.NEW_VERSION_HEADER, );
          return;
        }
        if (getValue(err, "response.status") === UNAUTHORIZED_ERROR_STATUS) {
          isUnauthorized = true;
          setSessionStorage("samlToken", null);
          showReloadRequiredModal(UNAUTHORIZED_ERROR_STATUS, MessageType.UNAUTHORIZED_MESSAGE, MessageType.UNAUTHORIZED_ERROR_HEADER);
          return;
        }
        if (type && type === "file") {
          return err;
        }
        return err.response; //TBD
      });
  }

  static callAPI(method: Methods, url: string, ...rest: any[]) {
    return axios[method](url, ...rest)
      .then((response) => {
        resetUnauthorizedFlag();
        const responseData = utcDateToUser(response);
          return responseData;
      })
      .catch((err) => {
        if (isVersionConflict(err.response)) {
          showReloadRequiredModal(VERSION_CONFLICT_STATUS, MessageType.RELOAD_MESSAGE, MessageType.NEW_VERSION_HEADER);
          return;
        }
        if (getValue(err, "response.status") === UNAUTHORIZED_ERROR_STATUS) {
          isUnauthorized = true;
          setSessionStorage("samlToken", null);
          showReloadRequiredModal(UNAUTHORIZED_ERROR_STATUS, MessageType.UNAUTHORIZED_MESSAGE, MessageType.UNAUTHORIZED_ERROR_HEADER);
          return;
        } else {
          return ErrorResponse(err.response);
        }
      });
  }

  /**
   * Download a blob with progress callback.
   * @param method HTTP method
   * @param url API endpoint
   * @param header Request headers
   * @param onDownloadProgress Optional callback for progress (percent: number)
   */
  static getBlob(
    method: Methods,
    url: string,
    header: any,
    onDownloadProgress?: (percent: number) => void
  ) {
    return axios({
      url: url,
      method: method,
      headers: header.headers,
      responseType: "blob",
      onDownloadProgress: (progressEvent: any) => {
        if (progressEvent && progressEvent.total && onDownloadProgress) {
         const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onDownloadProgress && onDownloadProgress(percent);
        }
      },
    })
      .then((response) => {
        resetUnauthorizedFlag();
        return response;
      })
      .catch((err) => {
         if (getValue(err, "response.status") === UNAUTHORIZED_ERROR_STATUS) {
          isUnauthorized = true;
          setSessionStorage("samlToken", null);
          showReloadRequiredModal(UNAUTHORIZED_ERROR_STATUS, MessageType.UNAUTHORIZED_MESSAGE, MessageType.UNAUTHORIZED_ERROR_HEADER);
          return;
        }
        if (isVersionConflict(err.response)) {
          showReloadRequiredModal(VERSION_CONFLICT_STATUS, MessageType.RELOAD_MESSAGE, MessageType.NEW_VERSION_HEADER);
          return;
        }
        return ErrorResponse(err.response);
      });
  }

  //-----------------------------------------------
  static async APIResponse(
    method: Methods,
    url: string,
    token: any,
    data?: any[],
    isPostPut?: boolean,
    onDownloadProgress?: (percent: number) => void
  ) {
    let header;
    const TempToken = getSessionStorage("samlToken");
    if (loggingURLArray.some((endpoint) => url.includes(endpoint))) {
      const apiVersion = await APP_VERSION;
      header = {
        headers: {
          Authorization: `Bearer ${!isNil(TempToken) && TempToken.length > 0 ? TempToken : token}`,
          "X-API-Version": apiVersion,
        },
      };
    } else {
      header = {
        headers: {
          Authorization: `Bearer ${!isNil(TempToken) && TempToken.length > 0 ? TempToken : token}`,
        },
      };
    }
    const fileType = data as any;
    if (fileType && (fileType === "excel" || fileType === "file")) {
      return API.getBlob(method, url, header, onDownloadProgress);
    } else if (isPostPut) {
      return API.call(method, url, "", data, header);
    } else if (method === "get" || method === "delete") {
      return API.call(method, url, data, header);
    } else {
      return API.callAPI(method, url, data, header);
    }
  }
  //-----------------------------------------------
  static APICall(
    method: Methods,
    url: string,
    data?: any[],
    isPostPut?: boolean,
    onDownloadProgress?: (percent: number) => void
  ) {
    if (isUnauthorized) return Promise.resolve(undefined);

     const TempToken = getSessionStorage("samlToken");
    if (!isNil(TempToken) && TempToken.length > 0) { 
     return API.APIResponse(
            method,
            url,
            `${TempToken}`,
            data,
            isPostPut,
            onDownloadProgress
          );
    }
    const currentAccounts = msalInstance.getAllAccounts();
    msalInstance.setActiveAccount(currentAccounts[0]);
    const accessTokenRequest = {
      scopes: [b2cScope],
      account: currentAccounts[0],
    };
    return msalInstance
      .acquireTokenSilent(accessTokenRequest)
      .then((accessTokenResponse: any) => {
        if (accessTokenResponse) {
          return API.APIResponse(
            method,
            url,
            accessTokenResponse.accessToken,
            data,
            isPostPut,
            onDownloadProgress
          );
        }
      })
      .catch((error) => {
        if (error instanceof InteractionRequiredAuthError) {
          msalInstance
            .acquireTokenPopup(accessTokenRequest)
            .then(function (response) {
              return API.APIResponse(
                method,
                url,
                response,
                data,
                isPostPut,
                onDownloadProgress
              );
            });
        }
      });
  }

  static getApi(url: string, config?: any) {
    return API.call("get", url, config).then((response) => {
      return response;
    });
  }

  static get(url: string, type?: any, onDownloadProgress?: (percent: number) => void) {
    return API.APICall("get", url, type, undefined, onDownloadProgress);
  }

  static post(url: string, data: any) {
    return API.APICall("post", url, data, true);
  }
  static put(url: string, data: any) {
    return API.APICall("put", url, data, true);
  }
  static delete(url: string) {
    return API.APICall("delete", url);
  }
  static putApi(url: string, data: any) {
    return API.APICall("put", url, data);
  }
  static postApi(url: string, data?: any) {
    return API.APICall("post", url, data);
  }

  static callWithHeader(
    method: Methods,
    url: string,
    headers?: any,
    data?: any
  ) {
    return axios({
      url: url,
      method: method,
      data: data,
      headers: headers,
    })
      .then((response) => {
        return response;
      })
      .catch((err) => {
         if (getValue(err, "response.status") === UNAUTHORIZED_ERROR_STATUS) {
          isUnauthorized = true;
          setSessionStorage("samlToken", null);
          showReloadRequiredModal(UNAUTHORIZED_ERROR_STATUS, MessageType.UNAUTHORIZED_MESSAGE, MessageType.UNAUTHORIZED_ERROR_HEADER);
          return;
        }
        return ErrorResponse(err.response);
      });
  }

  static getWithoutB2C(url: string, headers: any) {
    return API.callWithHeader("get", url, headers);
  }
  static postWithoutB2C(url: string, data: any, headers?: any) {
    return API.callWithHeader("post", url, headers, data);
  }
  static patchWithoutB2C(url: string, data: any, headers?: any) {
    return API.callWithHeader("patch", url, headers, data);
  }
}
export default API;
